import React, { useEffect, useState } from "react";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, DeleteCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Input,
  Textarea,
  Chip,
  Card,
  CardBody,
} from "@heroui/react";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import moment from "moment";
import { PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, ClockIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import Select from "react-select";

const Todo: React.FC = () => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  // Form States
  const [title, setTitle] = useState<string>("");
  const [detail, setDetail] = useState<string>("");
  const [notifyTime, setNotifyTime] = useState<string>("");
  const [shareUser1, setShareUser1] = useState<any>(null);
  const [shareUser2, setShareUser2] = useState<any>(null);
  const [shareUser3, setShareUser3] = useState<any>(null);
  const [editingTodoId, setEditingTodoId] = useState<any>(null);

  // User List
  const [userList, setUserList] = useState<any>([]);

  // Todo List
  const [pendingTodos, setPendingTodos] = useState<any>([]);
  const [completedTodos, setCompletedTodos] = useState<any>([]);

  // Fetch Users
  const { refetch: refetchUsers } = useQuery(
    ["UsersList"],
    () => ListCommanWay("/admin/user/list", { isActive: true }, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt = [];
          data.data.data.forEach((p) => {
            opt.push({
              value: p.id,
              label: p.name + " (" + p.username + ")",
            });
          });
          setUserList(opt);
        }
      },
    }
  );

  // Fetch Pending Todos
  const { refetch: refetchPendingTodos, isLoading: isLoadingPending } = useQuery(
    ["PendingTodoList"],
    () =>
      ListCommanWay(
        "/admin/todo/list",
        { status: "pending" },
        {
          paginate: 10000,
          order: [["createdAt", "DESC"]],
          include: ["_doneBy", "_shareUser1", "_shareUser2", "_shareUser3"],
        }
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setPendingTodos(data.data.data || []);
        }
      },
    }
  );

  // Fetch Completed Todos (Last 10 days)
  const { refetch: refetchCompletedTodos, isLoading: isLoadingCompleted } = useQuery(
    ["CompletedTodoList"],
    () => {
      const tenDaysAgo = moment().subtract(10, "days").format("YYYY-MM-DD");
      return ListCommanWay(
        "/admin/todo/list",
        { status: "completed", updatedAt: { $gte: tenDaysAgo } },
        {
          paginate: 10000,
          order: [["updatedAt", "DESC"]],
          include: ["_doneBy", "_shareUser1", "_shareUser2", "_shareUser3"],
        }
      );
    },
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setCompletedTodos(data.data.data || []);
        }
      },
    }
  );

  const refetchTodos = () => {
    refetchPendingTodos();
    refetchCompletedTodos();
  };

  const isLoadingTodos = isLoadingPending || isLoadingCompleted;
  const todoList = [...pendingTodos, ...completedTodos];

  // Create Todo
  const { mutate: mutateCreateTodo, isLoading: isCreating } = useMutation(
    (data: any) =>
      CreateCommanWay({
        url: "/admin/todo/create",
        data: data,
      }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("Todo Created Successfully", "success");
          resetForm();
          refetchTodos();
          onOpenChange();
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Update Todo
  const { mutate: mutateUpdateTodo, isLoading: isUpdating } = useMutation((data: any) => UpdateCommanWay(data), {
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        UseToast("Todo Updated Successfully", "success");
        resetForm();
        refetchTodos();
        onOpenChange();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  // Delete Todo
  const { mutate: mutateDeleteTodo } = useMutation((todoId: any) => DeleteCommanWay({ url: `/admin/todo/delete/${todoId}`, data: {} }), {
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        UseToast("Todo Deleted Successfully", "success");
        refetchTodos();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  // Quick Status Update
  const { mutate: mutateQuickUpdateStatus } = useMutation((data: any) => UpdateCommanWay(data), {
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        UseToast("Status Updated", "success");
        refetchTodos();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const resetForm = () => {
    setTitle("");
    setDetail("");
    setNotifyTime("");
    setShareUser1(null);
    setShareUser2(null);
    setShareUser3(null);
    setEditingTodoId(null);
  };

  const handleSubmit = () => {
    if (!title) {
      UseToast("Please enter a title", "error");
      return;
    }

    const todoData = {
      title,
      detail,
      notifyTime: notifyTime || undefined,
      shareUser1: shareUser1?.value || undefined,
      shareUser2: shareUser2?.value || undefined,
      shareUser3: shareUser3?.value || undefined,
      status: "pending",
    };

    if (editingTodoId) {
      mutateUpdateTodo({
        url: `/admin/todo/update/${editingTodoId}`,
        data: todoData,
      });
    } else {
      mutateCreateTodo(todoData);
    }
  };

  const handleEdit = (todo: any) => {
    setTitle(todo.title || "");
    setDetail(todo.detail || "");
    setNotifyTime(
      todo.notifyTime && todo.notifyTime != "Invalid Date" ? moment(todo.notifyTime, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DDTHH:mm") : ""
    );

    // Set shared users
    if (todo.shareUser1) {
      const user1 = userList.find((u) => u.value === todo.shareUser1);
      setShareUser1(user1 || null);
    }
    if (todo.shareUser2) {
      const user2 = userList.find((u) => u.value === todo.shareUser2);
      setShareUser2(user2 || null);
    }
    if (todo.shareUser3) {
      const user3 = userList.find((u) => u.value === todo.shareUser3);
      setShareUser3(user3 || null);
    }

    setEditingTodoId(todo.id);
    onOpen();
  };

  const handleDelete = (todoId: any) => {
    if (window.confirm("Are you sure you want to delete this todo?")) {
      mutateDeleteTodo(todoId);
    }
  };

  const handleOpenAddModal = () => {
    resetForm();
    onOpen();
  };

  const handleToggleStatus = (todo: any) => {
    const newStatus = todo.status === "completed" ? "pending" : "completed";
    const message = newStatus === "completed" ? "Mark this todo as completed?" : "Mark this todo as pending?";

    if (window.confirm(message)) {
      const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
      const currentUserId = userD?.state?.user?.id;

      mutateQuickUpdateStatus({
        url: `/admin/todo/update/${todo.id}`,
        data: {
          status: newStatus,
          doneBy: newStatus === "completed" ? currentUserId : undefined,
        },
      });
    }
  };

  return (
    <>
      <div className="">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">My Todos</h2>
          <Button size="sm" color="primary" startContent={<PlusIcon className="h-4 w-4" />} onClick={handleOpenAddModal}>
            Add Todo
          </Button>
        </div>

        {isLoadingTodos ? (
          <div className="text-center py-8">
            <Spinner />
          </div>
        ) : todoList.length === 0 ? (
          <div className="text-center text-gray-500 py-8">No todos found</div>
        ) : (
          <div className="space-y-2">
            {todoList.map((todo: any) => (
              <Card key={todo.id} className={`${todo.status === "completed" ? "bg-slate-300" : ""}`}>
                <CardBody className="p-3">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 pt-0.5">
                      <input
                        type="checkbox"
                        checked={todo.status === "completed"}
                        onChange={() => handleToggleStatus(todo)}
                        className="w-5 h-5 cursor-pointer"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 font-inter">
                      <h4 className={`font-semibold text-sm mb-1 underline ${todo.status === "completed" ? "line-through text-gray-500" : ""}`}>
                        {todo.title}
                      </h4>
                      {todo.detail && (
                        <p className={`text-xs text-gray-600 mb-2 ${todo.status === "completed" ? "line-through" : ""}`}>{todo.detail}</p>
                      )}
                      {todo.notifyTime && todo.notifyTime != "Invalid Date" && (
                        <p className="text-xs text-orange-600 flex items-center gap-1">
                          <ClockIcon className="h-3 w-3" />
                          {todo.notifyTime}
                        </p>
                      )}
                      {(todo.shareUser1 || todo.shareUser2 || todo.shareUser3) && (
                        <div className="flex items-center gap-1 mt-1">
                          <UserGroupIcon className="h-3 w-3 text-blue-500" />
                          <span className="text-xs text-blue-600">
                            Shared with:{" "}
                            {[
                              todo._shareUser1?.name || todo.shareUser1,
                              todo._shareUser2?.name || todo.shareUser2,
                              todo._shareUser3?.name || todo.shareUser3,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                        </div>
                      )}
                      {todo.status === "completed" && (
                        <div className="text-xs text-green-600 mt-1">
                          <p>Completed: {todo.updatedAt}</p>
                          {todo.doneBy && <p>Completed by: {todo._doneBy?.name || todo.doneBy}</p>}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => handleEdit(todo)} className="text-blue-600 hover:text-blue-800 p-1">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(todo.id)} className="text-red-600 hover:text-red-800 p-1">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Todo Modal */}
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="2xl"
        placement="center"
        isDismissable={false}
        classNames={{ wrapper: "z-[1002]" }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{editingTodoId ? "Edit Todo" : "Add New Todo"}</ModalHeader>
              <ModalBody>
                <div className="space-y-4 [&_input]:border-none [&_input]:px-0 [&_textarea]:border-none [&_textarea]:px-0">
                  <Input
                    label="Title"
                    labelPlacement="outside-top"
                    variant="bordered"
                    value={title}
                    onValueChange={setTitle}
                    placeholder="Enter todo title"
                    isRequired
                  />
                  <Textarea
                    label="Details"
                    labelPlacement="outside-top"
                    variant="bordered"
                    value={detail}
                    onValueChange={setDetail}
                    placeholder="Enter todo details"
                    minRows={3}
                  />
                  <Input
                    type="datetime-local"
                    label="Notify Time"
                    labelPlacement="outside-top"
                    variant="bordered"
                    value={notifyTime}
                    onValueChange={setNotifyTime}
                  />
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Share with Users</label>
                    <div className="space-y-2">
                      <Select
                        options={userList}
                        value={shareUser1}
                        onChange={setShareUser1}
                        placeholder="Select User 1"
                        className="text-sm"
                        isClearable
                      />
                      <Select
                        options={userList}
                        value={shareUser2}
                        onChange={setShareUser2}
                        placeholder="Select User 2"
                        className="text-sm"
                        isClearable
                      />
                      <Select
                        options={userList}
                        value={shareUser3}
                        onChange={setShareUser3}
                        placeholder="Select User 3"
                        className="text-sm"
                        isClearable
                      />
                    </div>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleSubmit} isLoading={isCreating || isUpdating}>
                  {editingTodoId ? "Update Todo" : "Add Todo"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default Todo;
